import { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Stack,
  useTheme,
  Chip,
  Avatar,
  Collapse,
} from "@mui/material";
import {
  Speed,
  Schedule,
  Person,
  LocalGasStation,
  AttachMoney,
  WatchLater,
  ExpandMore,
  ExpandLess,
  Circle as CircleIcon,
  Build,
  CalendarToday,
  CheckCircle,
} from "@mui/icons-material";
import { tokens } from "../../theme";
import { format } from "date-fns";

// InfoRow Component - for displaying information in a row format
const InfoRow = ({ icon, label, value }) => (
  <Box
    display="flex"
    alignItems="center"
    gap={2}
    sx={{
      py: 1,
      borderBottom: "1px solid rgba(255,255,255,0.1)",
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 40,
        height: 40,
        borderRadius: "50%",
        backgroundColor: "rgba(255,255,255,0.1)",
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1">{value || "N/A"}</Typography>
    </Box>
  </Box>
);

// InfoItem Component
const InfoItem = ({ icon, label, value }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <Grid item xs={12} sm={6} md={4}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          p: 2,
          backgroundColor: colors.primary[500],
          borderRadius: 1,
          transition: "transform 0.2s",
          "&:hover": {
            transform: "translateY(-2px)",
          },
        }}
      >
        <Box
          sx={{
            backgroundColor: colors.greenAccent[600],
            p: 1,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="body2" color={colors.grey[100]}>
            {label}
          </Typography>
          <Typography variant="h6" fontWeight="bold">
            {value || "N/A"}
          </Typography>
        </Box>
      </Box>
    </Grid>
  );
};

// TripCard Component

const TripCard = ({ trip, colors }) => {
  const [expanded, setExpanded] = useState(false);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return colors.greenAccent[500];
      case "scheduled":
        return colors.blueAccent[500];
      case "in progress":
        return colors.orangeAccent[500];
      case "cancelled":
        return colors.redAccent[500];
      default:
        return colors.grey[500];
    }
  };

  return (
    <Card
      sx={{
        backgroundColor: colors.primary[400],
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: 3,
        },
      }}
    >
      <CardContent>
        <Grid container spacing={2}>
          {/* Header Section */}
          <Grid item xs={12}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {trip.startLocation} → {trip.endLocation}
                </Typography>
                <Typography variant="body2" color={colors.grey[300]}>
                  {format(new Date(trip.date), "PPP")}
                </Typography>
              </Box>
              <Chip
                label={trip.status}
                sx={{
                  backgroundColor: getStatusColor(trip.status),
                  color: colors.primary[400],
                }}
              />
            </Box>
          </Grid>

          {/* Basic Info Section */}
          <Grid item xs={12} sm={4}>
            <Box display="flex" flexDirection="column" gap={1}>
              <InfoRow
                icon={<Schedule sx={{ color: colors.grey[300] }} />}
                label="Time"
                value={`${trip.startTime} - ${trip.endTime}`}
              />
              <InfoRow
                icon={<Speed sx={{ color: colors.grey[300] }} />}
                label="Distance"
                value={`${trip.distance} km`}
              />
              <InfoRow
                icon={<Person sx={{ color: colors.grey[300] }} />}
                label="Passengers"
                value={trip.passengers}
              />
            </Box>
          </Grid>

          {/* Route Section */}
          <Grid item xs={12} sm={4}>
            <Box>
              <Typography variant="body2" color={colors.grey[300]} mb={1}>
                Route
              </Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                {trip.stops?.map((stop, index) => (
                  <Box key={index} display="flex" alignItems="center" gap={1}>
                    <CircleIcon
                      sx={{
                        fontSize: 8,
                        color:
                          index === 0
                            ? colors.greenAccent[500]
                            : index === trip.stops.length - 1
                            ? colors.redAccent[500]
                            : colors.grey[500],
                      }}
                    />
                    <Typography variant="body2">{stop}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Grid>

          {/* Stats Section */}
          <Grid item xs={12} sm={4}>
            <Box display="flex" flexDirection="column" gap={1}>
              <InfoRow
                icon={<LocalGasStation sx={{ color: colors.grey[300] }} />}
                label="Fuel Used"
                value={`${trip.stats?.fuelConsumed || 0} L`}
              />
              <InfoRow
                icon={<AttachMoney sx={{ color: colors.grey[300] }} />}
                label="Revenue"
                value={`₹ ${trip.stats?.revenue || 0}`}
              />
              <InfoRow
                icon={<WatchLater sx={{ color: colors.grey[300] }} />}
                label="Delays"
                value={`${trip.stats?.delays || 0} mins`}
              />
            </Box>
          </Grid>

          {/* Expandable Section */}
          <Grid item xs={12}>
            <Button
              fullWidth
              onClick={() => setExpanded(!expanded)}
              endIcon={expanded ? <ExpandLess /> : <ExpandMore />}
              sx={{
                mt: 1,
                color: colors.grey[100],
              }}
            >
              {expanded ? "Show Less" : "Show More"}
            </Button>
            <Collapse in={expanded} timeout="auto">
              <Box mt={2}>
                <Typography
                  variant="subtitle2"
                  color={colors.grey[300]}
                  gutterBottom
                >
                  Driver Information
                </Typography>
                <Box display="flex" gap={2} alignItems="center">
                  <Avatar sx={{ bgcolor: colors.greenAccent[500] }}>
                    {trip.driver?.name?.charAt(0) || "D"}
                  </Avatar>
                  <Box>
                    <Typography variant="body2">
                      {trip.driver?.name || "Driver Name Not Available"}
                    </Typography>
                    <Typography variant="body2" color={colors.grey[300]}>
                      {trip.driver?.phone || "Phone Not Available"}
                    </Typography>
                  </Box>
                </Box>

                {/* Additional Trip Details */}
                <Box mt={2}>
                  <Typography
                    variant="subtitle2"
                    color={colors.grey[300]}
                    gutterBottom
                  >
                    Trip Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color={colors.grey[300]}>
                        Average Speed
                      </Typography>
                      <Typography variant="body2">
                        {trip.averageSpeed || 0} km/h
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color={colors.grey[300]}>
                        Total Time
                      </Typography>
                      <Typography variant="body2">
                        {trip.totalTime || "N/A"}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Collapse>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
// MaintenanceView Component

// For now changing this to be a pro version feature only
const MaintenanceView = ({ vehicle, colors }) => {
  // Default maintenance items if none provided
  const maintenanceItems = [
    {
      id: 1,
      title: "Regular Service",
      date: new Date(),
      status: "Completed",
      type: "Routine",
      description: "Regular maintenance check",
      cost: 5000,
      nextDue: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days from now
    },
    {
      id: 2,
      title: "Tire Change",
      date: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000), // 15 days ago
      status: "Completed",
      type: "Repair",
      description: "Changed all four tires",
      cost: 12000,
      nextDue: new Date(Date.now() + 180 * 24 * 60 * 60 * 1000), // 180 days from now
    },
    {
      id: 3,
      title: "Oil Change",
      date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days from now
      status: "Scheduled",
      type: "Routine",
      description: "Regular oil change",
      cost: 2000,
      nextDue: null,
    },
  ];

  return (
    <Grid container spacing={3}>
      {/* Maintenance Summary */}
      <Grid item xs={12}>
        <Card
          sx={{ backgroundColor: colors?.primary?.[400] || "#1F2A40", mb: 3 }}
        >
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Maintenance Summary
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: colors?.primary?.[500] || "#141b2d",
                    borderRadius: 1,
                    textAlign: "center",
                  }}
                >
                  <Build
                    sx={{
                      fontSize: 40,
                      color: colors?.greenAccent?.[500] || "#4cceac",
                    }}
                  />
                  <Typography variant="h4">
                    {
                      maintenanceItems.filter(
                        (item) => item.status === "Completed"
                      ).length
                    }
                  </Typography>
                  <Typography>Completed</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: colors?.primary?.[500] || "#141b2d",
                    borderRadius: 1,
                    textAlign: "center",
                  }}
                >
                  <CalendarToday
                    sx={{
                      fontSize: 40,
                      color: colors?.blueAccent?.[500] || "#6870fa",
                    }}
                  />
                  <Typography variant="h4">
                    {
                      maintenanceItems.filter(
                        (item) => item.status === "Scheduled"
                      ).length
                    }
                  </Typography>
                  <Typography>Scheduled</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: colors?.primary?.[500] || "#141b2d",
                    borderRadius: 1,
                    textAlign: "center",
                  }}
                >
                  <AttachMoney
                    sx={{
                      fontSize: 40,
                      color: colors?.redAccent?.[500] || "#db4f4a",
                    }}
                  />
                  <Typography variant="h4">
                    ₹
                    {maintenanceItems.reduce(
                      (acc, item) => acc + (item.cost || 0),
                      0
                    )}
                  </Typography>
                  <Typography>Total Cost</Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Maintenance History */}
      <Grid item xs={12} md={8}>
        <Card sx={{ backgroundColor: colors?.primary?.[400] || "#1F2A40" }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Maintenance History
            </Typography>
            <Stack spacing={2}>
              {maintenanceItems.map((item) => (
                <Card
                  key={item.id}
                  sx={{
                    backgroundColor: colors?.primary?.[500] || "#141b2d",
                    "&:hover": {
                      backgroundColor: colors?.primary?.[600] || "#101624",
                    },
                  }}
                >
                  <CardContent>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box>
                        <Typography variant="h6">{item.title}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {format(new Date(item.date), "PPP")}
                        </Typography>
                      </Box>
                      <Chip
                        icon={
                          item.status === "Completed" ? (
                            <CheckCircle />
                          ) : (
                            <Schedule />
                          )
                        }
                        label={item.status}
                        color={
                          item.status === "Completed" ? "success" : "warning"
                        }
                      />
                    </Box>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {item.description}
                    </Typography>
                    <Box display="flex" justifyContent="space-between" mt={2}>
                      <Typography variant="body2" color="text.secondary">
                        Type: {item.type}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Cost: ₹{item.cost}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      {/* Upcoming Maintenance */}
      <Grid item xs={12} md={4}>
        <Card sx={{ backgroundColor: colors?.primary?.[400] || "#1F2A40" }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Upcoming Maintenance
            </Typography>
            <Stack spacing={2}>
              {maintenanceItems
                .filter((item) => item.status === "Scheduled")
                .map((item) => (
                  <Box
                    key={item.id}
                    sx={{
                      p: 2,
                      backgroundColor: colors?.primary?.[500] || "#141b2d",
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="subtitle1">{item.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Scheduled for: {format(new Date(item.date), "PPP")}
                    </Typography>
                  </Box>
                ))}
            </Stack>
            <Button
              fullWidth
              variant="contained"
              sx={{
                mt: 2,
                backgroundColor: colors?.greenAccent?.[600] || "#4cceac",
                "&:hover": {
                  backgroundColor: colors?.greenAccent?.[700] || "#3da58a",
                },
              }}
            >
              Schedule Maintenance
            </Button>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

// Performance Data
const performanceData = [
  {
    id: "Speed",
    color: "hsl(217, 70%, 50%)",
    data: [
      { x: "Jan", y: 65 },
      { x: "Feb", y: 59 },
      { x: "Mar", y: 80 },
      { x: "Apr", y: 72 },
      { x: "May", y: 68 },
      { x: "Jun", y: 75 },
    ],
  },
  {
    id: "Distance",
    color: "hsl(127, 70%, 50%)",
    data: [
      { x: "Jan", y: 1200 },
      { x: "Feb", y: 1100 },
      { x: "Mar", y: 1400 },
      { x: "Apr", y: 1300 },
      { x: "May", y: 1250 },
      { x: "Jun", y: 1350 },
    ],
  },
];

// StatCards Component
const StatCards = ({ data, colors }) => (
  <Stack spacing={2}>
    <Card sx={{ backgroundColor: colors.primary[400], p: 2 }}>
      <Typography variant="h6" color={colors.grey[100]}>
        Total Distance
      </Typography>
      <Typography variant="h4" color={colors.greenAccent[500]}>
        {formatDistance(data.totalDistance || 0)}
      </Typography>
    </Card>
    <Card sx={{ backgroundColor: colors.primary[400], p: 2 }}>
      <Typography variant="h6" color={colors.grey[100]}>
        Average Speed
      </Typography>
      <Typography variant="h4" color={colors.greenAccent[500]}>
        {data.averageSpeed || 0} km/h
      </Typography>
    </Card>
    <Card sx={{ backgroundColor: colors.primary[400], p: 2 }}>
      <Typography variant="h6" color={colors.grey[100]}>
        Fuel Efficiency
      </Typography>
      <Typography variant="h4" color={colors.greenAccent[500]}>
        {data.fuelEfficiency || 0} km/l
      </Typography>
    </Card>
  </Stack>
);

// Helper function to format distance
const formatDistance = (distance) => {
  if (distance >= 1000) {
    return `${(distance / 1000).toFixed(1)}k km`;
  }
  return `${distance} km`;
};

export { InfoItem, TripCard, MaintenanceView, StatCards, performanceData };
